// Built-in US insurer directory — used by the Insurance Contacts popup and
// as suggestions for the insurer field on claims (keeps names consistent).
export const INSURERS = [
  { name: 'State Farm',           phone: '1-800-732-5246', web: 'statefarm.com/claims' },
  { name: 'GEICO',                phone: '1-800-841-3000', web: 'geico.com/claims' },
  { name: 'Progressive',          phone: '1-800-776-4737', web: 'progressive.com/claims' },
  { name: 'Allstate',             phone: '1-800-255-7828', web: 'allstate.com/claims' },
  { name: 'USAA',                 phone: '1-800-531-8722', web: 'usaa.com/claims' },
  { name: 'Farmers',              phone: '1-800-435-7764', web: 'farmers.com/claims' },
  { name: 'Liberty Mutual',       phone: '1-800-225-2467', web: 'libertymutual.com/claims' },
  { name: 'Nationwide',           phone: '1-800-421-3535', web: 'nationwide.com/claims' },
  { name: 'Travelers',            phone: '1-800-252-4633', web: 'travelers.com/claims' },
  { name: 'CONNECT (Costco)',     phone: '1-888-404-5365', web: 'costco.amfam.com/claims' },
  { name: 'American Family',      phone: '1-800-692-6326', web: 'amfam.com/claims' },
  { name: 'Chubb',                phone: '1-800-252-4670', web: 'chubb.com/claims' },
  { name: 'Auto-Owners',          phone: '1-888-252-4626', web: 'auto-owners.com/claims' },
  { name: 'The Hartford',         phone: '1-800-243-5860', web: 'thehartford.com/claims' },
  { name: 'Erie Insurance',       phone: '1-800-367-3743', web: 'erieinsurance.com/claims' },
  { name: 'MetLife Auto',         phone: '1-800-854-6011', web: 'metlife.com/claims' },
  { name: 'AAA',                  phone: '1-800-672-5246', web: 'aaa.com/claims' },
  { name: 'CSAA Insurance',       phone: '1-800-222-4242', web: 'csaa.com/claims' },
  { name: 'Safeco',               phone: '1-800-332-3226', web: 'safeco.com/claims' },
  { name: 'Kemper',               phone: '1-800-353-6737', web: 'kemper.com/claims' },
  { name: 'Bristol West',         phone: '1-800-274-7865', web: 'bristolwest.com/claims' },
  { name: 'Infinity Insurance',   phone: '1-855-478-3705', web: 'infinityauto.com/claims' },
  { name: 'Tesla Insurance',      phone: '1-844-348-3752', web: 'tesla.com/insurance/claims' },
  { name: 'Mercury Insurance',    phone: '1-800-503-3724', web: 'mercuryinsurance.com/claims' },
  { name: '21st Century',         phone: '1-888-244-6163', web: '21st.com/claims' },
  { name: 'Wawanesa',             phone: '1-888-929-2252', web: 'wawanesa.com/us/claims' },
  { name: 'California Casualty',  phone: '1-800-800-9410', web: 'calcas.com/claims' },
  { name: 'Esurance',             phone: '1-800-378-7262', web: 'esurance.com/claims' },
  { name: 'Lemonade',             phone: '1-844-733-8666', web: 'lemonade.com/claims' },
  { name: 'Anchor General',       phone: '1-800-542-6246', web: 'anchorgeneral.com/claims' },
];

export const STAGES = [
  'Car In',
  'Appraiser Inspection',
  'Waiting on Approval',
  'Total Loss',
  'SIU',
  'Dismantle',
  'Supplement Requested',
  'Waiting on Supplement',
  'Waiting on Parts',
  'Repair & Paint',
  'Closed',
];

export const STAGE_COLORS = {
  'Car In': '#6366f1',
  'Appraiser Inspection': '#8b5cf6',
  'Waiting on Approval': '#eab308',
  'Total Loss': '#ef4444',
  'SIU': '#f97316',
  'Dismantle': '#f97316',
  'Supplement Requested': '#ef4444',
  'Waiting on Supplement': '#ec4899',
  'Waiting on Parts': '#06b6d4',
  'Repair & Paint': '#22c55e',
  'Closed': '#6b7280',
};

// Special "off-pipeline" stages that get a full-modal tinted theme (like a flag).
// The `cls` class drives CSS variables (see index.css). cardBg/cardBorder style
// the floor card. Returns null for normal stages.
export function specialStageTheme(status) {
  if (status === 'Total Loss') return { cls: 'tl-modal', cardBg: '#33101a', cardBorder: 'rgba(239,68,68,0.6)' };
  if (status === 'SIU') return { cls: 'siu-modal', cardBg: '#33220c', cardBorder: 'rgba(249,115,22,0.6)' };
  return null;
}

export function stageIndex(status) {
  return STAGES.indexOf(status);
}

export function stageProgress(status) {
  if (status === 'Total Loss' || status === 'SIU') return 100;
  const idx = stageIndex(status);
  if (idx < 0) return 0;
  return Math.round(((idx + 1) / STAGES.length) * 100);
}

// ── WARNINGS ──────────────────────────────────────────────────────────────
// A claim is "stuck" after this many days in the same stage.
export const STUCK_DAYS = 10;
export const STUCK_DAYS_HIGH = 21;

function hasVal(s) { return s != null && String(s).trim() !== ''; }

export function daysSince(ts) {
  if (!ts) return 0;
  return Math.floor((Date.now() - new Date(ts).getTime()) / 86400000);
}

// stageSince = ISO timestamp the claim entered its current stage (from the latest
// activity_log status_change). Falls back to created_at when not provided.
// Returns an array of { type, severity, label, detail }.
export function getClaimWarnings(claim, stageSince) {
  const warnings = [];
  if (!claim || claim.status === 'Closed') return warnings; // done — no warnings

  // 1) Stuck in stage too long (skip flagged states Total Loss / SIU)
  if (claim.status !== 'Total Loss' && claim.status !== 'SIU') {
    const days = daysSince(stageSince || claim.created_at);
    if (days >= STUCK_DAYS) {
      warnings.push({
        type: 'stuck',
        severity: days >= STUCK_DAYS_HIGH ? 'high' : 'med',
        label: `${days}d in stage`,
        detail: `In "${claim.status}" for ${days} days`,
      });
    }
  }

  // 2) Missing info — only the fields critical to processing a claim
  const missing = [];
  if (!hasVal(claim.claim_num)) missing.push('claim #');
  if (!hasVal(claim.adjuster)) missing.push('adjuster');
  if (missing.length) {
    warnings.push({
      type: 'missing',
      severity: 'med',
      label: 'Missing info',
      detail: `Missing ${missing.join(', ')}`,
    });
  }

  return warnings;
}

export function timeAgo(dateStr) {
  const now = new Date();
  const then = new Date(dateStr);
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return then.toLocaleDateString();
}
